/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useSession } from "@packages/sdk";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import type { TextProps } from "../../../_base/_core/Text/Text.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const desktopMenuStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: colors.transparent,
  ":hover": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.primary,
  },
  ":active": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.neutralsMedHigh,
  },
  borderRadius: radius.m,
  color: theme.semanticColors.primary,
  cursor: "pointer",
  padding: numericPixels[16],
  width: spacing.full,
});

export type LogoutButton = {
  size?: TextProps["size"];
  type?: TextProps["type"];
};

export const LogoutButton = forwardRef<any, LogoutButton>(
  ({ size = "m", type = "button" }, ref) => {
    const analytics = useAnalytics();
    const isDesktop = useIsDesktopViewport();
    const t = useTranslations();
    const { logout } = useSession();

    const clickLogout = () =>
      logout({
        callback: () => {
          analytics.track({
            event: HallowAnalyticsEvent.TappedLogOut,
          });
          analytics.logout();
          location.reload();
        },
      });

    return (
      <Text
        ref={ref}
        onClick={() => clickLogout()}
        css={isDesktop ? desktopMenuStyle : undefined}
        {...({ size, type } as TextProps)}
      >
        {t("logout_title")}
      </Text>
    );
  },
);

LogoutButton.displayName = "LogoutButton";
